"use client"

import { usePathname, useSearchParams } from "next/navigation"
import { useEffect, Suspense, useRef, useState } from "react"
import { usePostHog, PostHogProvider as PHProvider } from "posthog-js/react"

import posthog from "posthog-js"
import { usePosthogIdentified } from "@/hooks/usePosthogIdentified"
import { identifyAuthenticatedPostHogUser } from "@/lib/posthog/identity"
import { registerEmbeddedStandaloneWebphoneDiagnostics } from "@/lib/posthog/embeddedDiagnostics"
import { getPageViewCaptureDecision, POSTHOG_DEFAULTS_DATE, POSTHOG_UI_HOST, runOnce } from "@/lib/posthog/clientConfig"
import { getPostHogPersistence, shouldForcePostHogSessionRecording, toPostHogRuntimeContext } from "@/lib/posthog/runtime"
import type { TLoginUserData } from "@/schemas/user.schema"

type PostHogLogsConfig = {
  logs?: {
    captureConsoleLogs?: boolean
    serviceName?: string
    environment?: string
    serviceVersion?: string
  }
}

function buildPostHogPageViewUrl(pathname: string, search: string): string {
  const url = new URL(pathname || "/", window.location.origin)
  if (search) {
    url.search = search
  }
  return url.toString()
}

export function PostHogProvider({ children, initialUserData }: { children: React.ReactNode; initialUserData?: TLoginUserData }) {
  const [isPostHogReady, setIsPostHogReady] = useState(false)
  const [initialPageViewUrl, setInitialPageViewUrl] = useState<string | null>(null)
  const authenticatedDistinctId = initialUserData?.account.status !== "LOGGED_OUT" && initialUserData?.user.id ? initialUserData.user.id.toString() : null

  useEffect(() => {
    const runtimeContext = toPostHogRuntimeContext(window)
    let hasRegisteredDiagnostics = false
    let diagnosticsCleanup = () => {}

    const markReady = runOnce((client: typeof posthog) => {
      if (!hasRegisteredDiagnostics) {
        diagnosticsCleanup = registerEmbeddedStandaloneWebphoneDiagnostics(client, runtimeContext)
        hasRegisteredDiagnostics = true
      }

      if (authenticatedDistinctId && initialUserData) {
        identifyAuthenticatedPostHogUser({
          account: initialUserData.account,
          client,
          storage: window.localStorage,
          user: initialUserData.user,
        })
      }

      const currentUrl = buildPostHogPageViewUrl(window.location.pathname, window.location.search)
      client.capture("$pageview", { $current_url: currentUrl })
      setInitialPageViewUrl(currentUrl)
      setIsPostHogReady(true)
    })

    posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY as string, {
      api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || "https://l.ring.io",
      ui_host: POSTHOG_UI_HOST,
      person_profiles: "identified_only", // or 'always' to create profiles for anonymous users as well
      capture_pageview: false, // Disable automatic pageview capture, as we capture manually
      capture_pageleave: true, // Enable pageleave capture
      capture_heatmaps: true, // Enable heatmap capture
      capture_exceptions: true, // Enable exception capture
      capture_dead_clicks: true, // Enable dead click capture
      capture_performance: true, // Enable performance capture
      enable_heatmaps: true, // Enable heatmap capture
      enable_recording_console_log: true, // Enable recording console log
      session_recording: {
        // WARNING: Only enable this if you understand the security implications
        recordCrossOriginIframes: true,
      },
      persistence: getPostHogPersistence(runtimeContext),
      ...(process.env.NEXT_PUBLIC_POSTHOG_LOGS_ENABLED === "true"
        ? ({
            logs: {
              captureConsoleLogs: false,
              serviceName: process.env.NEXT_PUBLIC_NAME || "ringio-front-web",
              environment: process.env.NEXT_PUBLIC_ENV || process.env.NODE_ENV || "unknown",
              serviceVersion: process.env.NEXT_PUBLIC_VERSION || "unknown",
            },
          } satisfies PostHogLogsConfig)
        : {}),
      defaults: POSTHOG_DEFAULTS_DATE,
      loaded: (client) => {
        markReady(client as typeof posthog)
      },
    })

    if (shouldForcePostHogSessionRecording(runtimeContext)) {
      posthog.startSessionRecording(true)
    }

    if ("__loaded" in posthog && posthog.__loaded) {
      markReady(posthog)
    }

    return () => {
      diagnosticsCleanup()
    }
  }, [authenticatedDistinctId, initialUserData])

  return (
    <PHProvider client={posthog}>
      {isPostHogReady ? <SuspendedPostHogPageView authenticatedDistinctId={authenticatedDistinctId} initialPageViewUrl={initialPageViewUrl} /> : null}
      {children}
    </PHProvider>
  )
}

function PostHogPageView({ authenticatedDistinctId, initialPageViewUrl }: { authenticatedDistinctId: string | null; initialPageViewUrl: string | null }) {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const posthog = usePostHog()
  const hasHandledInitialPageViewRef = useRef(false)

  usePosthogIdentified(authenticatedDistinctId)

  // Track pageviews
  useEffect(() => {
    if (pathname && posthog) {
      const url = buildPostHogPageViewUrl(pathname, searchParams.toString())
      const decision = getPageViewCaptureDecision({
        currentUrl: url,
        hasHandledInitialPageView: hasHandledInitialPageViewRef.current,
        initialPageViewUrl,
      })
      hasHandledInitialPageViewRef.current = decision.hasHandledInitialPageView

      if (!decision.capture) {
        return
      }

      posthog.capture("$pageview", { $current_url: url })
    }
  }, [initialPageViewUrl, pathname, searchParams, posthog])

  return null
}

// Keep route pageview tracking isolated from the client initialization lifecycle.
function SuspendedPostHogPageView({ authenticatedDistinctId, initialPageViewUrl }: { authenticatedDistinctId: string | null; initialPageViewUrl: string | null }) {
  return (
    <Suspense fallback={null}>
      <PostHogPageView authenticatedDistinctId={authenticatedDistinctId} initialPageViewUrl={initialPageViewUrl} />
    </Suspense>
  )
}
